import { useEffect, useState, useCallback } from "react";
import { tFactory } from "@/i18n";
import { api } from "@/lib/api";
import { LANGUAGES } from "@/components/LanguagePersonaBar";

const RTL = new Set(["ar", "ur", "he", "fa"]);
const SUPPORTED = new Set(LANGUAGES.map((l) => l.code));

/** Detect the learner's browser/system language on very first visit.
 *  Returns a 2-letter code that belongs to our supported set, or "en". */
function detectBrowserLang() {
  if (typeof navigator === "undefined") return "en";
  const cands = [navigator.language, ...(navigator.languages || [])].filter(Boolean);
  for (const raw of cands) {
    const code = String(raw).toLowerCase().split(/[-_]/)[0];
    if (SUPPORTED.has(code)) return code;
  }
  return "en";
}

// Global store — shared across components so changing the language anywhere
// instantly re-renders every consumer.
let _lang = "en";
if (typeof window !== "undefined") {
  const saved = localStorage.getItem("oak_lang");
  if (saved && SUPPORTED.has(saved)) {
    _lang = saved;
  } else {
    // First-contact: auto-pick from browser + persist.
    _lang = detectBrowserLang();
    try { localStorage.setItem("oak_lang", _lang); } catch {}
  }
  // Sync <html lang> + dir on module init so SPA navigations + full page
  // reloads both land with the correct locale attribute. Without this, the
  // visible UI translates but the HTML `lang` attr stays "en" on refresh.
  if (typeof document !== "undefined") {
    document.documentElement.lang = _lang;
    document.documentElement.dir = RTL.has(_lang) ? "rtl" : "ltr";
  }
}

const _listeners = new Set();

function setLang(lang) {
  _lang = lang;
  try { localStorage.setItem("oak_lang", lang); } catch {}
  if (typeof document !== "undefined") {
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL.has(lang) ? "rtl" : "ltr";
  }
  _listeners.forEach((fn) => fn(lang));
}

export function useT() {
  const [lang, setLocal] = useState(_lang);

  useEffect(() => {
    _listeners.add(setLocal);
    // Initial sync from server prefs — server wins only if it has a saved,
    // non-default preference (otherwise keep the browser-detected value).
    api.get("/user/prefs").then(({ data }) => {
      if (data?.language && SUPPORTED.has(data.language) && data.language !== _lang) {
        // Server has a persisted pick — honour it.
        if (data.has_saved) {
          setLang(data.language);
        } else if (_lang === "en" && data.language !== "en") {
          setLang(data.language);
        }
      }
      // If the server doesn't know about us yet AND our detected lang isn't
      // the default "en", push it once so the backend can generate AI content
      // in the right language from the first request.
      if (data && !data.has_saved && _lang !== "en") {
        api.put("/user/prefs", { language: _lang, persona: "expert" }).catch(() => {});
      }
    }).catch(() => {});
    return () => _listeners.delete(setLocal);
  }, []);

  const t = useCallback(tFactory(lang), [lang]);
  const changeLang = useCallback((l) => {
    setLang(l);
    api.put("/user/prefs", { language: l, persona: "expert" }).catch(() => {});
  }, []);

  return { t, lang, setLang: changeLang, isRTL: RTL.has(lang) };
}
